import { Testimonial } from '~/components/ui/Testimonial'
import { useState } from 'react'
import { YouTubeEmbed } from '~/components/ui/YouTubeEmbed'
import { createFileRoute } from '@tanstack/react-router'
import { pageMeta } from '~/lib/site/seo'
import { cacheHeaders } from '~/lib/site/cache'
import { getAboutPage, getCoreValues } from '~/lib/content/content'
import type { AboutPage } from '~/lib/content/content'
import { Button } from '~/components/ui/Button'
import { bodyClass } from '~/components/ui/BodyText'
import { Container } from '~/components/ui/Container'
import { InlineHtml } from '~/components/ui/InlineHtml'
import { MediaImg } from '~/components/ui/MediaImg'
import { SectionHeading } from '~/components/ui/SectionHeading'
import { DonateSignupBand } from '~/components/sections/DonateSignupBand'
import { PanelSection } from '~/components/sections/PanelSection'
import { Eyebrow } from '~/components/ui/Eyebrow'
import {
  MissionStatement,
  ReportViolationNote,
} from '~/components/sections/CoreValuesCopy'
import { UnderlineAccordion } from '~/components/sections/UnderlineAccordion'
import { PageTitle } from '~/components/ui/PageTitle'
import { PageHero } from '~/components/ui/PageHero'

const TITLE = 'About Us | Direct Action Everywhere'
const DESCRIPTION =
  'Learn who we are: DxE is a global grassroots network of animal rights activists working for social and political change for animals in one generation.'

// The copy is edited in content/about.json (docs/content.md); the layout,
// video and core values stay here.
export const Route = createFileRoute('/about-us')({
  loader: async () => ({
    page: await getAboutPage(),
    coreValues: await getCoreValues(),
  }),
  headers: () => cacheHeaders.content,
  head: () => pageMeta({ title: TITLE, description: DESCRIPTION }),
  component: AboutUs,
})

function AboutUs() {
  const { page, coreValues } = Route.useLoaderData()
  return (
    <>
      <PageHero>
        <Container width="narrow">
          <Eyebrow>about us</Eyebrow>
          <PageTitle size="md" className="mt-2.5 mb-0">
            {page.title}
          </PageTitle>
          <YouTubeEmbed
            id="I65LCZbGje4"
            query="rel=0&controls=1&cc_load_policy=1"
            title="What is Direct Action Everywhere?"
            className="mt-16 md:mt-10"
          />
        </Container>
      </PageHero>

      <PanelSection
        eyebrow="our mission"
        className="pt-20"
        innerClassName="max-w-[840px] md:text-center"
      >
        <SectionHeading className="mt-5 mb-8">
          {page.mission.heading}
        </SectionHeading>
        <MissionStatement
          paragraphs={page.mission.paragraphs}
          className={bodyClass}
        />
      </PanelSection>

      <section className="pt-[60px]">
        <Container width="narrow">
          <Testimonial attribution={page.testimonial.attribution}>
            {page.testimonial.quote}
          </Testimonial>
        </Container>
      </section>

      <Accomplishments {...page.accomplishments} />

      <PanelSection
        eyebrow="Our core values"
        className="pt-20"
        innerClassName="max-w-[840px]"
      >
        <div className="mt-10">
          <UnderlineAccordion
            items={coreValues.map((v, i) => ({
              key: v.strong,
              title: `${i + 1}. ${v.before}${v.strong}${v.after}`,
              body: (
                <div className={`${bodyClass} space-y-5`}>
                  {v.paragraphs.map((p) => (
                    <p key={p} className="m-0">
                      {p}
                    </p>
                  ))}
                </div>
              ),
            }))}
          />
        </div>
        <ReportViolationNote className="mt-5 mb-0 text-sm leading-6" />
      </PanelSection>

      <section className="pt-20">
        <Container>
          <Eyebrow>Our Global Steering Committee</Eyebrow>
          <p className="mt-2.5 mb-10 text-sm leading-5">
            <InlineHtml html={page.team.intro} />
          </p>
          <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-5 lg:gap-6">
            {page.team.members.map((m) => (
              <div key={m.name} className="text-center">
                <MediaImg
                  image={m.image}
                  sizes="220px"
                  alt={m.name}
                  className="mx-auto aspect-square w-full max-w-[220px] rounded-full object-cover"
                />
                <div className="mt-5 text-base font-semibold text-fg">
                  {m.name}
                </div>
                <p className="mt-2.5 mb-0 text-sm leading-5">
                  <InlineHtml html={m.bio} />
                </p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-20 text-center">
        <Container>
          <Eyebrow>Financial transparency</Eyebrow>
          <p className="mt-5 mb-8 text-base leading-6 md:text-lg">
            <InlineHtml html={page.financial.html} />
          </p>
          <Button
            href={page.financial.href}
            target="_blank"
            rel="noreferrer"
            variant="outline"
          >
            {page.financial.button}
          </Button>
        </Container>
      </section>

      <DonateSignupBand />
    </>
  )
}

/** The first `shown` items are always visible; the rest follow "Learn more". */
function Accomplishments({
  image,
  imageAlt,
  items,
  shown,
}: AboutPage['accomplishments']) {
  const [expanded, setExpanded] = useState(false)
  return (
    <section className="pt-20">
      <Container className="grid items-center gap-10 md:grid-cols-2">
        <MediaImg
          image={image}
          sizes="(min-width: 768px) 45vw, 90vw"
          alt={imageAlt}
          className="w-full rounded-sm"
        />
        <div className={`${bodyClass} max-w-[560px]`}>
          <Eyebrow>Our Accomplishments</Eyebrow>
          <ul className="mt-5 list-disc space-y-5 pl-6">
            {(expanded ? items : items.slice(0, shown)).map((html) => (
              <li key={html}>
                <InlineHtml html={html} />
              </li>
            ))}
          </ul>
          {items.length > shown && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              className="mt-5 cursor-pointer text-sm font-semibold text-accent hover:underline"
            >
              {expanded ? 'Show Less' : 'Learn more'}
            </button>
          )}
        </div>
      </Container>
    </section>
  )
}
